import { Component, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacyTable as MatTable } from '@angular/material/legacy-table';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { filter, finalize, map, switchMap, takeUntil } from 'rxjs/operators';

import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { LegacyDialogsService, PresetSettings } from 'cloud-shared-lib';
import { CuttingMaterial, MaterialType } from '../../models/cutting/cutting-material.model';
import { AngleMode } from '../../models/cutting/cutting-panel-outline.model';
import { CuttingPanel } from '../../models/cutting/cutting-panel.model';
import { CuttingSettings } from '../../models/user/cutting-settings.model';
import { CsvSchemaSelectorDialogComponent } from './csv-schema-selector-dialog/csv-schema-selector-dialog.component';
import { CuttingPanelListService } from './cutting-panel-list.service';
import { FieldSettingsComponent } from './field-settings/field-settings.component';
import { ImportFromCsvDialogComponent } from './import-from-csv-dialog/import-from-csv-dialog.component';
import { PanelListDataService } from './panel-list-data.service';
import { PanelMirroringDialogComponent } from './panel-mirroring-dialog/panel-mirroring-dialog.component';
import {
  OutlineEditorTabsEnum,
  PanelOutlineEditorDialogComponent,
} from './panel-outline-editor-dialog/panel-outline-editor-dialog.component';

export type TablePanelListCol = 'position' | 'height' | 'width' | 'count';

@Component({
  selector: 'csl-panel-list',
  templateUrl: 'panel-list.component.html',
  styleUrls: ['panel-list.component.scss'],
})
export class PanelListComponent implements OnChanges, OnDestroy {
  private displayedColumns = [
    'position',
    'name',
    'height',
    'width',
    'count',
    'orientation',
    'l1',
    'l2',
    'w1',
    'w2',
    'editedAngles',
    'slotCount',
    'fastenersExists',
    'attachment',
    'outlineEditor',
    'faceBandingExists',
    'remark',
  ];

  currentPanelsState = false;
  thisOutlineEditorTabsEnum = OutlineEditorTabsEnum;
  fieldSettingsDialogRef: MatDialogRef<FieldSettingsComponent>;
  outlineEditorDialogRef: MatDialogRef<PanelOutlineEditorDialogComponent>;
  panelMirroringDialogRef: MatDialogRef<PanelMirroringDialogComponent>;
  panelsImportCsvDialogRef: MatDialogRef<ImportFromCsvDialogComponent>;
  private unsubscribeAll$: Subject<void> = new Subject();
  currentScreenWidth = '';
  editPanelPosition = true;

  materialGroupsWithPostforming: Set<number> = new Set();

  csvExport$: Observable<Blob>;

  @Input() userId = 0;
  @Input() orderId: number;
  @Input() mainMaterial: CuttingMaterial;
  @Input() materialWidth = 0;
  @Input() inMatBaseIdMaterial = 0;
  @Input() panels: CuttingPanel[] = [];
  @Input() cuttingSettings: CuttingSettings;
  @Input() presetSettings: PresetSettings;

  @ViewChild(MatTable) table: MatTable<any>;

  get panelSetHasAttachments(): boolean {
    return this.panels.some(p => p.attachmentName > '');
  }

  get panelSetHasButts(): boolean {
    return this.panels.some(p => this.hasButts(p));
  }

  get maxNumberPrecision(): number {
    return this.cuttingSettings?.panels.maxNumberPrecision || 0;
  }

  constructor(
    private cuttingPanelListService: CuttingPanelListService,
    public dialog: MatDialog,
    private panelListDataService: PanelListDataService,
    private dialogService: LegacyDialogsService,
    private breakpointObserver: BreakpointObserver
  ) {
    this.cuttingPanelListService.onCuttingOptionsChanged
      .asObservable()
      .pipe(
        filter(res => !!res),
        takeUntil(this.unsubscribeAll$)
      )
      .subscribe(res => {
        this.materialGroupsWithPostforming = new Set(
          res.postforming.filter(m => m.editableWidth).map(m => m.inMatBaseId)
        );
      });

    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
      .pipe(
        takeUntil(this.unsubscribeAll$),
        filter(result => result.matches),
        map(result => {
          if (result.breakpoints[Breakpoints.XSmall]) return 'xs';
          if (result.breakpoints[Breakpoints.Small]) return 'sm';
          if (result.breakpoints[Breakpoints.Medium]) return 'md';
          if (result.breakpoints[Breakpoints.Large]) return 'lg';
          if (result.breakpoints[Breakpoints.XLarge]) return 'xl';
        })
      )
      .subscribe(screenWidth => {
        if (screenWidth !== this.currentScreenWidth) {
          this.currentScreenWidth = screenWidth;
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.cuttingSettings?.currentValue) {
      this.cuttingPanelListService.onCuttingOptionsChanged.next(this.cuttingSettings);
    }
  }

  getDisplayedColumns(): string[] {
    let dc: string[] = this.displayedColumns;

    if (
      this.mainMaterial.type === MaterialType.Linear &&
      !this.materialGroupsWithPostforming.has(this.mainMaterial.inMaterialBaseGroupId)
    ) {
      dc = dc.filter(c => c !== 'width');
    }

    if (this.mainMaterial.type === MaterialType.Linear) {
      dc = dc.filter(c => c !== 'orientation');
    }

    if (this.currentScreenWidth === 'xs' || this.currentScreenWidth === 'sm') {
      // gt-sm
      dc = dc.filter(
        c => ['remark', 'l1', 'l2', 'w1', 'w2', 'editedAngles', 'slotCount', 'fastenersExists'].indexOf(c) === -1
      );
    }

    if (!this.presetSettings.componentsAvailability.outlineEditor.outlineEditor) {
      dc = dc.filter(
        c =>
          [
            'l1',
            'l2',
            'w1',
            'w2',
            'editedAngles',
            'slotCount',
            'fastenersExists',
            'attachment',
            'outlineEditor',
          ].indexOf(c) === -1
      );
    }

    return dc.filter(c => (this.panelSetHasButts ? true : c !== 'l1' && c !== 'l2' && c !== 'w1' && c !== 'w2'));
  }

  getSlotCount(panelUuid: string): number {
    const panel = this.panels.find(p => p.uuid === panelUuid);

    let res = panel.panelOutline.topSide.groove ? 1 : 0;
    res = res + (panel.panelOutline.rightSide.groove ? 1 : 0);
    res = res + (panel.panelOutline.bottomSide.groove ? 1 : 0);
    res = res + (panel.panelOutline.leftSide.groove ? 1 : 0);

    return res;
  }

  isAngleEditing(panelUuid: string): boolean {
    const panel = this.panels.find(p => p.uuid === panelUuid);

    return (
      panel.panelOutline.topLeft.mode !== AngleMode.None ||
      panel.panelOutline.topRight.mode !== AngleMode.None ||
      panel.panelOutline.bottomRight.mode !== AngleMode.None ||
      panel.panelOutline.bottomLeft.mode !== AngleMode.None
    );
  }

  isFastenersExists(panelUuid: string): boolean {
    const panel = this.panels.find(p => p.uuid === panelUuid);

    return (
      panel.panelOutline.topSide.customHoles.length !== 0 ||
      panel.panelOutline.rightSide.customHoles.length !== 0 ||
      panel.panelOutline.bottomSide.customHoles.length !== 0 ||
      panel.panelOutline.leftSide.customHoles.length !== 0 ||
      panel.panelOutline.topSide.fastenersSets.length !== 0 ||
      panel.panelOutline.rightSide.fastenersSets.length !== 0 ||
      panel.panelOutline.bottomSide.fastenersSets.length !== 0 ||
      panel.panelOutline.leftSide.fastenersSets.length !== 0
    );
  }

  isFaceBandingExists(panelUuid: string): boolean {
    const panel = this.panels.find(p => p.uuid === panelUuid);

    return panel.panelOutline.faceBanding.length > 0;
  }

  hasButts(panel: CuttingPanel): boolean {
    return panel.l1 || panel.l2 || panel.w1 || panel.w2 ? true : false;
  }

  hasCornersProcessing(panel: CuttingPanel): boolean {
    return (
      panel.panelOutline.topLeft.mode !== AngleMode.None ||
      panel.panelOutline.topRight.mode !== AngleMode.None ||
      panel.panelOutline.bottomRight.mode !== AngleMode.None ||
      panel.panelOutline.bottomLeft.mode !== AngleMode.None
    );
  }

  hasCutouts(panel: CuttingPanel): boolean {
    return !_.isEmpty(panel.panelOutline.cutouts);
  }

  /*******************************TOOLS*************************************/

  exportCsv(): void {
    this.csvExport$ = this.dialogService.component(CsvSchemaSelectorDialogComponent, null, 500).pipe(
      filter(res => !!res),
      switchMap(res =>
        this.panelListDataService.downloadPanelsCsv(this.orderId, this.inMatBaseIdMaterial, this.panels, res.id)
      )
    );
  }

  openOutlineEditorDialog(panelUuid: string, activeTab = OutlineEditorTabsEnum.Butts): void {
    const panel = this.panels.find(p => p.uuid === panelUuid);

    this.outlineEditorDialogRef = this.dialog.open(PanelOutlineEditorDialogComponent, {
      panelClass: 'panel-outline-editor-dialog',
      disableClose: true,
      minWidth: '1000px',
      width: '80vw',
    });

    this.outlineEditorDialogRef.componentInstance.adminMode = true;
    this.outlineEditorDialogRef.componentInstance.orderId = this.orderId;
    this.outlineEditorDialogRef.componentInstance.activeTab = activeTab;
    this.outlineEditorDialogRef.componentInstance.matInMatBaseId = this.inMatBaseIdMaterial;
    this.outlineEditorDialogRef.componentInstance.panelMaterial = panel;
    this.outlineEditorDialogRef.componentInstance.mainMaterial = this.mainMaterial;
    this.outlineEditorDialogRef.componentInstance.editable = false;
    this.outlineEditorDialogRef.componentInstance.presetSettings = this.presetSettings;

    this.outlineEditorDialogRef
      .afterClosed()
      .pipe(finalize(() => (this.outlineEditorDialogRef = null)))
      .subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll$.next();
  }
}
